import { useCallback, useEffect, useRef, useState } from 'react';
import { twinChangedOf, updateTwin } from '@repo/front-end-services';
import type { AdminTwin } from '@repo/frontend-types';
import { normaliseServerMessage } from '@repo/front-end-utils';

import { clashSentence, TWIN_REQUEST_TIMEOUT_MS, type TwinDetailsPatch } from './twinDetailsForm';
import type { DraftSaveStatus } from './useDraftSaver';

/**
 * The Twin details step's writes (`twin-details-page-address`, R6): every change saves straight to
 * the twin through `PATCH /api/twins/:bid`.
 *
 * At page level, beside `useOpeningHoursEditor`, so a save already queued finishes when the person
 * moves to another step (*Next: Opening Hours* never waits, R7.1), and so the title bar's twin tag
 * can read it.
 *
 * **One write at a time.** A change made while one is in flight is merged into one held patch per
 * twin (later values win per key) and sent when the first settles. The builders in
 * `twinDetailsForm.ts` only ever set a value together with its source, so a merge cannot split
 * them. **A failed save holds** that twin's queue until *Retry saving*, which resends the held
 * patch: the PATCH sets values, so sending it twice leaves the same row (spec, Failure States).
 *
 * **A refused name is final for that name** (`twin-details-page-summary`, R3.5). A rename the server
 * refuses — a 400 or 409 whose `validationErrors` names only `name` — is not held for *Retry*: the
 * name is dropped from the held patch, the rest is sent on, and the refusal is reported for the twin
 * on screen (`nameRefusal`). Retrying would only be refused again.
 *
 * **Keyed by BID** (R6.5). Each held patch is for the twin it was made on, and is sent to that twin
 * even if the page has moved on; what the hook reports is only ever the twin on screen's.
 *
 * **Each save says what it last saw** (`onboarding-multiplayer-twin-record-checks`, R3). The queue
 * keeps, per twin, the stored value it knows for each field — from the twin reads the page and both
 * steps make (`noteRecord`) and from each accepted save's response row — and sends it as
 * `if_unchanged` for every field of a patch it has one for (never `camera_start_args`). A merge into
 * a held patch keeps the older base, so a run of edits is checked against where the person started.
 * **On a clash** (a 409 `twin_changed`) the stale fields are dropped and never resent, the rest of the
 * patch goes on, and `clash` asks the steps to read the twin again so they show the colleague's
 * values: *show theirs, drop mine*. The tag reads `blocked` — *Not saved* and the sentence, no
 * *Retry* — until the next accepted save of a change made after the clash, or another twin.
 *
 * Bounds (docs/coding-standards.md, Outbound calls): 20 s per attempt (`TWIN_REQUEST_TIMEOUT_MS`);
 * no automatic retry, the person's *Retry saving* only; no list read.
 */

export interface TwinDetailsSaveState {
  /** `blocked`: a clash dropped someone's change (R3.4); the tag's form with no *Retry*. */
  status: DraftSaveStatus;
  /**
   * `failed`: the server's sentence when it gave one (a 400 names the field), else `null`.
   * `blocked`: the clash sentence.
   */
  reason: string | null;
  /** A change not yet saved for the twin on screen: held, in flight or failed (R1.3). */
  pending: boolean;
}

/** The identity fields still on their way to the server; a key is present only when pending. */
export type UnsavedIdentity = Pick<TwinDetailsPatch, 'name' | 'description'>;

/** The position still on its way to the server; present only when pending. */
export type UnsavedPosition = Pick<TwinDetailsPatch, 'latitude' | 'longitude' | 'position_source'>;

/** An accepted description; `value: null` is an accepted clear. */
export interface SavedDescription {
  value: string | null;
}

/** A rename the server refused, for the twin on screen (`twin-details-page-summary`, R3.5). */
export interface NameRefusal {
  bid: string;
  /** The name that was refused, so the step can tell whether it is still the one typed. */
  name: string;
  /** The server's sentence for the name. */
  message: string;
}

export interface TwinDetailsSave extends TwinDetailsSaveState {
  /**
   * Queue a change for `bid`, the twin it was made on — by default the twin on screen. A no-op with
   * no twin or an empty patch.
   */
  save: (patch: TwinDetailsPatch, bid?: string | null) => void;
  retry: () => void;
  /**
   * The last refused rename for the twin on screen; cleared when another name is queued for it, or
   * dismissed once the person has left it.
   */
  nameRefusal: NameRefusal | null;
  /** The person closed the name on something other than the refused name: forget the refusal. */
  dismissNameRefusal: (bid: string) => void;
  /** The last name the server accepted for the twin on screen in this session, or `null`. */
  savedName: string | null;
  /**
   * The last description the server accepted for the twin on screen in this session, boxed so an
   * accepted clear (`{ value: null }`) is distinct from none accepted (`null`)
   * (`retire-edit-mode-twin-description`, R6).
   */
  savedDescription: SavedDescription | null;
  /**
   * The name and description handed to the queue for the twin on screen and not yet accepted: in
   * flight, held or failed, the newest value per key. A step that mounts while a save is pending
   * (the person left Twin details and came back) shows these rather than the record it reads, so a
   * card opened meanwhile never holds the old value and sends it back once the save lands.
   */
  unsaved: UnsavedIdentity | null;
  /**
   * The position handed to the queue for the twin on screen and not yet accepted, as `unsaved` is
   * for the identity: Position on map moves the twin too, so Twin details shows a position still
   * being saved there rather than the record it reads (`retire-edit-mode-position-write-path`).
   */
  unsavedPosition: UnsavedPosition | null;
  /**
   * Resolves once nothing is held or in flight for any twin: `true` when every change was saved,
   * `false` when one failed, has no token to send with, or the page unmounted first. Leaving the
   * wizard waits on it, because unmounting drops the queue (R6.3).
   */
  whenSettled: () => Promise<boolean>;
  /**
   * Record a twin as read: its stored values become what the next save of each field says it saw
   * (R3.1). Called where the page and the steps already read the twin. Pass the `readMark()` taken
   * before the read started: a field a save wrote after that point keeps the value the save wrote,
   * since the read may have been served before the write landed (review). `shown` says the read is
   * what the step now shows, so it replaces the name and description this session last saved.
   */
  noteRecord: (bid: string, twin: Partial<AdminTwin>, mark?: number, shown?: boolean) => void;
  /** A point in the queue's sequence of accepted saves, to take before reading the twin. */
  readMark: () => number;
  /**
   * The fields of a twin that a save accepted after `mark` wrote. A read that began at `mark` may be
   * older than them, so it must not put them on screen (Copilot review).
   */
  writtenSince: (bid: string, mark: number) => string[];
  /**
   * The last clash for the twin on screen: `seq` changes with each one, so a step reads the twin again
   * when it does (R3.3). `null` when there has been none, or it cleared.
   */
  clash: TwinClash | null;
}

export interface TwinClash {
  bid: string;
  /** The fields someone else changed, as the server named them. */
  fields: string[];
  seq: number;
}

/** The fields a save may say it saw: every patch field but the opening view, which is never checked. */
/**
 * Fields that travel together: a value and its source. A clash on one drops the whole group, because
 * the route refuses a source sent without its value (Copilot review).
 */
const CLASH_GROUPS: ReadonlyArray<ReadonlyArray<string>> = [
  ['latitude', 'longitude', 'position_source'],
  ['iana_timezone', 'timezone_source'],
];
/**
 * A map turn saves the orientation and the origin it moved as one placement, so where a patch carries
 * both, a clash on either drops both: half a turn is neither theirs nor mine (Copilot review). A held
 * patch cannot tell a turn from two separate edits, so both are dropped then too, which shows theirs.
 */
const TURN_GROUP: ReadonlyArray<string> = [
  'latitude',
  'longitude',
  'position_source',
  'orientation',
];
const carriesTurn = (patch: TwinDetailsPatch | undefined): boolean =>
  !!patch && 'orientation' in patch && ('latitude' in patch || 'longitude' in patch);

const CHECKED_FIELDS = [
  'name',
  'description',
  'address_1',
  'address_2',
  'city',
  'state',
  'postcode',
  'country',
  'latitude',
  'longitude',
  'position_source',
  'iana_timezone',
  'timezone_source',
  'orientation',
] as const satisfies ReadonlyArray<keyof TwinDetailsPatch>;

type Base = Partial<Record<string, unknown>>;

interface ClashEntry extends TwinClash {
  reason: string;
  /** A change was queued after the clash; its acceptance clears the clash. */
  changedSince: boolean;
  /** Clashed fields no read has brought back yet: a newer clash names them too (Copilot review). */
  unresolved: Set<string>;
}

interface Options {
  middlewareApi: string;
  accessToken: string | null;
  twinBid: string | null;
  /** Called after each patch the server accepted, with the twin it was for. */
  onSaved?: (bid: string, patch: TwinDetailsPatch) => void;
}

interface Failure {
  status: number | undefined;
  reason: string | null;
  /** Each refused field's sentences, joined — from the middleware's `validationErrors`. */
  refused: Record<string, string>;
}

const SAVED: TwinDetailsSaveState = { status: 'saved', reason: null, pending: false };

const failureOf = (error: unknown): Failure => {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } })
    ?.response;
  const status = response?.status;
  // A refusal (4xx) says what was wrong; anything else (5xx, a timeout, no answer) is the tag's
  // own *The twin could not be saved.*
  const reason =
    status != null && status >= 400 && status < 500 && response?.data?.message != null
      ? normaliseServerMessage(response.data.message, '') || null
      : null;
  // The middleware's one refusal envelope (`validationFailed`, `alreadyTaken`): a list of fields,
  // each with its sentences. Read only on a 400 or 409, the two that envelope is sent with.
  const refused: Record<string, string> = {};
  const listed = (response?.data as { validationErrors?: unknown } | undefined)?.validationErrors;
  if ((status === 400 || status === 409) && Array.isArray(listed)) {
    for (const item of listed as Array<{ field?: unknown; errors?: unknown }>) {
      if (typeof item?.field !== 'string') continue;
      const errors = Array.isArray(item.errors)
        ? item.errors.filter((e): e is string => typeof e === 'string')
        : [];
      refused[item.field] = errors.join(' ');
    }
  }
  return { status, reason, refused };
};

/** The failure refused the name and nothing else: final for that name (R3.5). */
const refusesOnlyName = (failure: Failure, patch: TwinDetailsPatch): boolean => {
  const fields = Object.keys(failure.refused);
  return patch.name !== undefined && fields.length === 1 && fields[0] === 'name';
};

export const useTwinDetailsSave = ({
  middlewareApi,
  accessToken,
  twinBid,
  onSaved,
}: Options): TwinDetailsSave => {
  /** Patches waiting to be sent, per twin; Map order is the order each twin first changed. */
  const heldRef = useRef(new Map<string, TwinDetailsPatch>());
  /** Each held patch's base: per key, the stored value it was made against (R3.2). */
  const heldBaseRef = useRef(new Map<string, Base>());
  const inFlightRef = useRef<{ bid: string; patch: TwinDetailsPatch; base: Base } | null>(null);
  /** The stored value known for each field, per twin (R3.1). */
  const knownRef = useRef(new Map<string, Base>());
  /** Accepted saves, counted; and per twin, per field, the count when a save last wrote it. */
  const writeSeqRef = useRef(0);
  const writtenAtRef = useRef(new Map<string, Record<string, number>>());
  /** The last clash, per twin (R3.4). */
  const clashesRef = useRef(new Map<string, ClashEntry>());
  const clashSeqRef = useRef(0);
  /** Twins whose last save failed: their held patch waits for *Retry saving*. */
  const failedRef = useRef(new Map<string, Failure>());

  const tokenRef = useRef(accessToken);
  tokenRef.current = accessToken;
  const twinBidRef = useRef(twinBid);
  twinBidRef.current = twinBid;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  /** The last refused rename, per twin. */
  const refusalsRef = useRef(new Map<string, NameRefusal>());
  /** The last accepted rename, per twin. */
  const savedNamesRef = useRef(new Map<string, string>());
  /** The last accepted description, per twin. A new box per acceptance, so its identity is stable. */
  const savedDescriptionsRef = useRef(new Map<string, SavedDescription>());
  const mountedRef = useRef(true);
  /** `whenSettled` callers waiting for the queue to go idle. */
  const waitersRef = useRef<Array<(saved: boolean) => void>>([]);
  useEffect(() => {
    mountedRef.current = true;
    // The array is only ever emptied in place, never replaced.
    const waiting = waitersRef.current;
    return () => {
      mountedRef.current = false;
      waiting.splice(0).forEach(resolve => resolve(false));
    };
  }, []);

  /** Nothing in flight, and every held patch is a failed one waiting for *Retry saving*. */
  const idle = useCallback(
    () =>
      !inFlightRef.current &&
      Array.from(heldRef.current.keys()).every(bid => failedRef.current.has(bid)),
    [],
  );
  const releaseWaiters = useCallback(() => {
    if (!idle() || waitersRef.current.length === 0) return;
    const saved = failedRef.current.size === 0;
    const waiters = waitersRef.current.splice(0);
    waiters.forEach(resolve => resolve(saved));
  }, [idle]);

  const stateFor = useCallback((bid: string | null): TwinDetailsSaveState => {
    if (!bid) return SAVED;
    const failure = failedRef.current.get(bid);
    if (failure) return { status: 'failed', reason: failure.reason, pending: true };
    if (inFlightRef.current?.bid === bid || heldRef.current.has(bid)) {
      return { status: 'saving', reason: null, pending: true };
    }
    const clash = clashesRef.current.get(bid);
    if (clash) return { status: 'blocked', reason: clash.reason, pending: false };
    return SAVED;
  }, []);

  const [state, setState] = useState<TwinDetailsSaveState>(SAVED);
  const [nameRefusal, setNameRefusal] = useState<NameRefusal | null>(null);
  const [savedName, setSavedName] = useState<string | null>(null);
  const [savedDescription, setSavedDescription] = useState<SavedDescription | null>(null);
  const [unsaved, setUnsaved] = useState<UnsavedIdentity | null>(null);
  const [unsavedPosition, setUnsavedPosition] = useState<UnsavedPosition | null>(null);
  const [clash, setClash] = useState<TwinClash | null>(null);
  const settle = useCallback(() => {
    if (!mountedRef.current) return;
    const bid = twinBidRef.current;
    const next = stateFor(bid);
    setState(prev =>
      prev.status === next.status && prev.reason === next.reason && prev.pending === next.pending
        ? prev
        : next,
    );
    const refusal = bid ? (refusalsRef.current.get(bid) ?? null) : null;
    setNameRefusal(prev => (prev === refusal ? prev : refusal));
    setSavedName(bid ? (savedNamesRef.current.get(bid) ?? null) : null);
    setSavedDescription(bid ? (savedDescriptionsRef.current.get(bid) ?? null) : null);
    const entry = bid ? clashesRef.current.get(bid) : undefined;
    setClash(prev =>
      entry
        ? prev?.bid === entry.bid && prev.seq === entry.seq
          ? prev
          : { bid: entry.bid, fields: entry.fields, seq: entry.seq }
        : null,
    );
    // In flight under held: the held patch is the newer one, so its values win per key.
    const inFlight = inFlightRef.current?.bid === bid ? inFlightRef.current.patch : undefined;
    const pending = { ...(inFlight ?? {}), ...((bid && heldRef.current.get(bid)) || {}) };
    const identity: UnsavedIdentity = {};
    if ('name' in pending) identity.name = pending.name;
    if ('description' in pending) identity.description = pending.description;
    const changed = Object.keys(identity).length > 0 ? identity : null;
    setUnsaved(prev => (JSON.stringify(prev) === JSON.stringify(changed) ? prev : changed));
    // The builders send the pair and its source together (wire rules 2 and 3).
    const position: UnsavedPosition | null =
      'latitude' in pending
        ? {
            latitude: pending.latitude,
            longitude: pending.longitude,
            position_source: pending.position_source,
          }
        : null;
    setUnsavedPosition(prev =>
      JSON.stringify(prev) === JSON.stringify(position) ? prev : position,
    );
  }, [stateFor]);

  // The twin on screen changed: report ITS queue, never the last twin's (R6.5). The clash sentence of
  // the twin left behind clears (R3.4), so coming back to it later does not bring back an old one.
  const shownBidRef = useRef(twinBid);
  useEffect(() => {
    const left = shownBidRef.current;
    shownBidRef.current = twinBid;
    if (left && left !== twinBid) clashesRef.current.delete(left);
    settle();
  }, [twinBid, settle]);

  const pump = useCallback(async () => {
    if (inFlightRef.current) return;
    const token = tokenRef.current;
    if (!token) return;
    const next = Array.from(heldRef.current).find(([bid]) => !failedRef.current.has(bid));
    if (!next) return;
    const [bid, patch] = next;
    const base = heldBaseRef.current.get(bid) ?? {};
    heldRef.current.delete(bid);
    heldBaseRef.current.delete(bid);
    // This attempt's own marker: a send it starts on its way out (the clash branch) installs another,
    // which its cleanup must leave alone (Copilot review).
    const mine = { bid, patch, base };
    inFlightRef.current = mine;
    settle();
    let saved = false;
    let row: Partial<AdminTwin> | null = null;
    // Only the keys being sent, and only those with a known base (R3.1).
    const ifUnchanged: Base = {};
    for (const key of Object.keys(patch)) {
      if (key in base) ifUnchanged[key] = base[key];
    }
    try {
      const result = await updateTwin(middlewareApi, token, bid, patch, {
        timeout: TWIN_REQUEST_TIMEOUT_MS,
        ...(Object.keys(ifUnchanged).length > 0 ? { ifUnchanged } : {}),
      });
      const data = (result as { data?: unknown } | undefined)?.data;
      row = data && typeof data === 'object' ? (data as Partial<AdminTwin>) : null;
      saved = true;
    } catch (error) {
      // A refusal naming no field cannot say what to drop, so it is held for Retry as any failure is,
      // never resent at once (review: resending the whole patch looped without end).
      const changed = twinChangedOf(error);
      if (changed && changed.fields.length > 0) {
        // R3.3: show theirs, drop mine. The stale fields leave the sent patch and any newer edit of
        // them (made against the same stale base), with the rest of any value-and-source group, and
        // are never resent; the rest goes on, not held for Retry.
        const stale = new Set(changed.fields);
        const newer = heldRef.current.get(bid);
        const groups =
          carriesTurn(patch) || carriesTurn(newer) ? [...CLASH_GROUPS, TURN_GROUP] : CLASH_GROUPS;
        for (const group of groups) {
          if (group.some(key => stale.has(key))) group.forEach(key => stale.add(key));
        }
        // eslint-disable-next-line no-console
        console.error('[twin-details] save failed', {
          bid,
          status: 409,
          fields: Object.keys(patch),
          clash: Array.from(stale),
        });
        const without = <T extends object>(o: T | undefined): Partial<T> =>
          Object.fromEntries(Object.entries(o ?? {}).filter(([k]) => !stale.has(k))) as Partial<T>;
        const newerBase = heldBaseRef.current.get(bid);
        const merged: TwinDetailsPatch = { ...without(patch), ...without(newer) };
        const mergedBase: Base = { ...without(newerBase), ...without(base) };
        if (Object.keys(merged).length > 0) {
          heldRef.current.set(bid, merged);
          heldBaseRef.current.set(bid, mergedBase);
        } else {
          heldRef.current.delete(bid);
          heldBaseRef.current.delete(bid);
        }
        // The stale fields keep their old known value until the re-read replaces it: an edit made
        // before then is refused again rather than written unchecked (Copilot review).
        // A name or description this session accepted earlier is no longer the twin's.
        if (stale.has('name')) savedNamesRef.current.delete(bid);
        if (stale.has('description')) savedDescriptionsRef.current.delete(bid);
        // A clash before the last one's re-read landed keeps its fields too: two clashes can reach a
        // step in one render, and the step would otherwise bring back only the newer's.
        const prev = clashesRef.current.get(bid);
        const named = new Set<string>(
          prev && !prev.changedSince ? Array.from(prev.unresolved) : [],
        );
        stale.forEach(key => named.add(key));
        clashSeqRef.current += 1;
        clashesRef.current.set(bid, {
          bid,
          fields: Array.from(named),
          seq: clashSeqRef.current,
          reason: clashSentence(Array.from(named), changed.modifiedAt),
          changedSince: false,
          unresolved: new Set(named),
        });
        inFlightRef.current = null;
        settle();
        void pump();
        releaseWaiters();
        return;
      }
      const failure = failureOf(error);
      const nameOnly = refusesOnlyName(failure, patch);
      // Field NAMES only — never the values, which hold a customer's address and the name tried
      // (spec, Access).
      // eslint-disable-next-line no-console
      console.error('[twin-details] save failed', {
        bid,
        status: failure.status ?? null,
        fields: Object.keys(patch),
        ...(nameOnly ? { refused: 'name' } : {}),
      });
      const newer = heldRef.current.get(bid);
      // R3.5: a refused name is final, so it is never sent again. A newer name typed meanwhile stays
      // queued, and then this refusal is about a name no longer in the field, so it is not said.
      const { name: sentName, ...rest } = patch;
      const nameRefused = sentName !== undefined && failure.refused.name !== undefined;
      if (nameRefused && newer?.name === undefined) {
        refusalsRef.current.set(bid, {
          bid,
          name: sentName,
          message: failure.refused.name || failure.reason || 'This name can’t be used.',
        });
      }
      // Put the sent patch back UNDER anything changed meanwhile, so Retry sends the newest values;
      // its base goes back OVER the newer one's, the older base winning (R3.2).
      const merged: TwinDetailsPatch = { ...(nameRefused ? rest : patch), ...(newer ?? {}) };
      const { name: _sentNameBase, ...restBase } = base;
      const mergedBase: Base = {
        ...(heldBaseRef.current.get(bid) ?? {}),
        ...(nameRefused ? restBase : base),
      };
      if (Object.keys(merged).length > 0) {
        heldRef.current.set(bid, merged);
        heldBaseRef.current.set(bid, mergedBase);
      } else {
        heldRef.current.delete(bid);
        heldBaseRef.current.delete(bid);
      }
      // The name alone refused: everything else goes on. Anything else waits for *Retry saving*.
      if (!nameOnly && heldRef.current.has(bid)) failedRef.current.set(bid, failure);
    } finally {
      if (inFlightRef.current === mine) inFlightRef.current = null;
    }
    if (saved) {
      // What the twin now holds: the response row where it carries the field (so server
      // normalisation never makes the next save look stale), else the value sent.
      const known = knownRef.current.get(bid) ?? {};
      writeSeqRef.current += 1;
      const writtenAt = writtenAtRef.current.get(bid) ?? {};
      for (const key of Object.keys(patch)) {
        if (key === 'camera_start_args') continue;
        writtenAt[key] = writeSeqRef.current;
        known[key] =
          row && key in row
            ? (row as Record<string, unknown>)[key]
            : (patch as Record<string, unknown>)[key];
      }
      knownRef.current.set(bid, known);
      writtenAtRef.current.set(bid, writtenAt);
      // A newer edit of a field this save just wrote is made against what was written.
      const heldBase = heldBaseRef.current.get(bid);
      if (heldBase) {
        for (const key of Object.keys(patch)) if (key in heldBase) heldBase[key] = known[key];
      }
      const entry = clashesRef.current.get(bid);
      if (entry?.changedSince) clashesRef.current.delete(bid);
      if (patch.name !== undefined) savedNamesRef.current.set(bid, patch.name);
      // `in`, not `!== undefined`: an accepted clear (`null`) is remembered too (R6).
      if ('description' in patch) {
        savedDescriptionsRef.current.set(bid, { value: patch.description ?? null });
      }
      onSavedRef.current?.(bid, patch);
    }
    settle();
    void pump();
    releaseWaiters();
  }, [middlewareApi, settle, releaseWaiters]);

  const save = useCallback(
    (patch: TwinDetailsPatch, forBid?: string | null) => {
      const bid = forBid ?? twinBidRef.current;
      if (!bid || Object.keys(patch).length === 0) return;
      if (patch.name !== undefined) refusalsRef.current.delete(bid);
      heldRef.current.set(bid, { ...(heldRef.current.get(bid) ?? {}), ...patch });
      // The base of each new key: what the twin will hold once anything in flight for it lands, else
      // what is known. An existing key keeps its older base (R3.2).
      const heldBase = heldBaseRef.current.get(bid) ?? {};
      const known = knownRef.current.get(bid) ?? {};
      const inFlight = inFlightRef.current?.bid === bid ? inFlightRef.current : null;
      for (const key of Object.keys(patch)) {
        if (key in heldBase || key === 'camera_start_args') continue;
        if (inFlight && key in inFlight.patch) {
          heldBase[key] = (inFlight.patch as Record<string, unknown>)[key];
        } else if (key in known) {
          heldBase[key] = known[key];
        }
      }
      heldBaseRef.current.set(bid, heldBase);
      const entry = clashesRef.current.get(bid);
      if (entry) entry.changedSince = true;
      settle();
      void pump();
    },
    [pump, settle],
  );

  const retry = useCallback(() => {
    const bid = twinBidRef.current;
    if (!bid || !failedRef.current.has(bid)) return;
    failedRef.current.delete(bid);
    settle();
    void pump();
  }, [pump, settle]);

  const dismissNameRefusal = useCallback(
    (bid: string) => {
      if (refusalsRef.current.delete(bid)) settle();
    },
    [settle],
  );

  const whenSettled = useCallback(
    () =>
      new Promise<boolean>(resolve => {
        if (idle()) resolve(failedRef.current.size === 0);
        // With no token nothing can be sent, so waiting would never end.
        else if (!tokenRef.current && !inFlightRef.current) resolve(false);
        else waitersRef.current.push(resolve);
      }),
    [idle],
  );

  const noteRecord = useCallback(
    (bid: string, twin: Partial<AdminTwin>, mark?: number, shown = false) => {
      const known = knownRef.current.get(bid) ?? {};
      const writtenAt = writtenAtRef.current.get(bid) ?? {};
      const clashEntry = clashesRef.current.get(bid);
      for (const key of Object.keys(twin)) clashEntry?.unresolved.delete(key);
      let forgot = false;
      for (const key of CHECKED_FIELDS) {
        if (!(key in twin)) continue;
        // Written by a save accepted after this read began: the read may predate the write.
        if (mark !== undefined && (writtenAt[key] ?? 0) > mark) continue;
        known[key] = (twin as Record<string, unknown>)[key] ?? null;
        // A read the step shows, no older than this session's last accepted name or description, is
        // what the twin holds now, so it replaces ours; else the step would show ours and check the
        // next edit against theirs (Copilot review).
        if (!shown) continue;
        if (key === 'name') forgot = savedNamesRef.current.delete(bid) || forgot;
        if (key === 'description') forgot = savedDescriptionsRef.current.delete(bid) || forgot;
      }
      knownRef.current.set(bid, known);
      if (forgot) settle();
    },
    [settle],
  );

  const readMark = useCallback(() => writeSeqRef.current, []);
  const writtenSince = useCallback((bid: string, mark: number) => {
    const writtenAt = writtenAtRef.current.get(bid) ?? {};
    return Object.keys(writtenAt).filter(key => writtenAt[key] > mark);
  }, []);

  // A token that arrives after a change was queued (a refresh) sends it.
  useEffect(() => {
    if (accessToken) void pump();
  }, [accessToken, pump]);

  return {
    ...state,
    save,
    retry,
    nameRefusal,
    dismissNameRefusal,
    savedName,
    savedDescription,
    unsaved,
    unsavedPosition,
    whenSettled,
    readMark,
    writtenSince,
    noteRecord,
    clash,
  };
};
